import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  Modal,
} from "@hid-galaxy-ui/galaxy-react";
import SummarySection from "./SummarySection";
import LicensesSection from "./LicensesSection";
import PointsAcceleratorsSection, {
  type PointsAcceleratorDisplayMetrics,
} from "./PointsAcceleratorsSection";
import EngagementActivitiesSection from "./EngagementActivitiesSection";
import CommentsFilesSection from "./CommentsFilesSection";
import "./Detail.scss";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import { getPartnerEngagementCategoryListSagaAction } from "../../../../../../../redux/saga/partner-program/engagement-configuration/saga-actions";
import {
  deletePartnerPoints,
  updatePartnerPoints,
} from "../../../../../../../redux/saga/partner-program/calculator/saga-action";
import { partnerProgramEventConfigurationSliceAction } from "../../../../../../../redux/slices/partner-program/calculator";
import { buildEvaluationDataFromRecord } from "./buildEvaluationDataFromRecord";
import CommentModal from "../calculator/CommentModal";
import AssessmentDayPicker from "./AssessmentDayPicker";
import moment from "moment";
import { axiosRestInstance } from "../../../../../../../common/axios";

/** Same pattern as reports-dashboard `reports-list` — resolve user id to display name. */
const getUserByIdAPI = async (userId: number) => {
  try {
    const res = await axiosRestInstance.get(`/user/getUser/?ID=${userId}`);
    return res.data;
  } catch {
    return null;
  }
};

const formatUserDisplayName = (data: {
  first_name?: string;
  last_name?: string;
}) =>
  [data?.first_name, data?.last_name].filter(Boolean).join(" ").trim();

interface DetailProps {
  onBack?: () => void;
  selectedAssesmentData: any;
  summaryDat: any;
  partnerId?: number;
  readOnly?: boolean;
  onRefresh?: () => void;
  /** Toast notifications (same pattern as partner-program consent / Galaxy Notification). */
  onMutationToast?: (message: string, isError: boolean) => void;
}

const getPointsRecordId = (record: any): number | null => {
  if (record == null) return null;
  const id =
    record.ID ?? record.id ?? record.assessment_id ?? record.ASSESSMENT_ID;
  return id != null ? Number(id) : null;
};

/** Per-row potential, same as calculator: max(option) − selected. */
const sumPotentialImprovementFromEngagementRows = (
  engagements: any[] | null | undefined
): number => {
  if (!Array.isArray(engagements) || !engagements.length) return 0;
  return engagements.reduce(
    (acc, e) => acc + (Number(e?.potential_improvement) || 0),
    0
  );
};

/** Σ potential_improvement × net new licenses; null if record has no engagements array (use API aggregate). */
const getPotentialPointsFromRecord = (record: any): number | null => {
  if (record == null) return null;
  const eng = record.engagements;
  if (!Array.isArray(eng)) return null;
  const netNew = Number(record.partner_new_licenses) || 0;
  return sumPotentialImprovementFromEngagementRows(eng) * netNew;
};

const Detail: React.FC<DetailProps> = ({
  onBack,
  selectedAssesmentData,
  summaryDat,
  partnerId,
  readOnly = false,
  onRefresh,
  onMutationToast,
}) => {
  const dispatch = useAppDispatch();
  const { userDetail } = useAppSelector((state) => state.user);
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { partnerLicenceList } = useAppSelector(
    (state) => state.partner_licenceProgram
  );
  const { partnerEngagementCategoryList } = useAppSelector(
    (state) => state.partner_engagementConfiguration
  );
  const {
    status: {
      updatePartnerPointsLoading,
      deletePartnerPointsLoading,
      partnerPointsMutationMessage,
    },
  } = useAppSelector((state) => state.partner_program_event_configuration);

  const [isEditing, setIsEditing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [evaluationData, setEvaluationData] = useState<any[]>([]);
  const [licenses, setLicenses] = useState({
    netNewLicenses: 0,
    totalLicenses: 0,
  });
  /** Snapshot of `partner_total_licenses` when edit starts (matches calculator `initialTotalLicenses`). */
  const [initialTotalLicenses, setInitialTotalLicenses] = useState(0);
  const [liveTotals, setLiveTotals] = useState({
    totalPoints: 0,
    potentialPoints: 0,
  });
  const [comment, setComment] = useState("");
  const [existingDocuments, setExistingDocuments] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  /** `YYYY-MM-DD` for assessment day (calculator-style picker while editing) */
  const [assessmentDateInput, setAssessmentDateInput] = useState("");

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentMainIndex, setCommentMainIndex] = useState(0);
  const [commentSubIndex, setCommentSubIndex] = useState(0);
  const [selectedComment, setSelectedComment] = useState<any[]>([]);
  const [selectedActivityName, setSelectedActivityName] = useState("");
  const [resolvedSubmitterName, setResolvedSubmitterName] = useState<
    string | null
  >(null);

  const wasUpdating = useRef(false);
  const wasDeleting = useRef(false);

  const computeExtraPoints = useCallback(
    (totalLicenses: number) => {
      if (!partnerLicenceList?.length) return 0;
      const sorted = [...partnerLicenceList].sort(
        (a, b) => a.license_count - b.license_count
      );
      for (let i = sorted.length - 1; i >= 0; i--) {
        if (totalLicenses >= sorted[i].license_count) {
          return sorted[i].points ?? 0;
        }
      }
      return 0;
    },
    [partnerLicenceList]
  );

  useEffect(() => {
    dispatch(getPartnerEngagementCategoryListSagaAction());
  }, [dispatch]);

  useEffect(() => {
    const nameFromApi =
      selectedAssesmentData?.submitted_by_name ??
      selectedAssesmentData?.submitted_by_user_name ??
      selectedAssesmentData?.created_by_name;

    if (nameFromApi && String(nameFromApi).trim() !== "") {
      setResolvedSubmitterName(null);
      return;
    }

    const rawId =
      selectedAssesmentData?.updated_by ??
      selectedAssesmentData?.submitted_by ??
      selectedAssesmentData?.created_by;
    const uid =
      rawId != null && rawId !== "" ? Number(rawId) : NaN;
    if (!Number.isFinite(uid) || uid <= 0) {
      setResolvedSubmitterName(null);
      return;
    }

    let cancelled = false;

    if (
      loggedUserDetail?.ID != null &&
      Number(loggedUserDetail.ID) === uid
    ) {
      setResolvedSubmitterName(
        formatUserDisplayName(loggedUserDetail) || null
      );
      return;
    }
    if (userDetail?.ID != null && Number(userDetail.ID) === uid) {
      const localName = formatUserDisplayName({
        first_name: userDetail?.first_name,
        last_name: userDetail?.last_name,
      });
      setResolvedSubmitterName(localName || null);
      return;
    }

    (async () => {
      const userRes = await getUserByIdAPI(uid);
      if (cancelled) return;
      if (userRes?.success && userRes?.data) {
        const n = formatUserDisplayName(userRes.data);
        setResolvedSubmitterName(n || `User ${uid}`);
      } else {
        setResolvedSubmitterName(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedAssesmentData, userDetail, loggedUserDetail]);

  const regionalFixed = useMemo(
    () =>
      Number(selectedAssesmentData?.regional_modifier ?? 1) || 1,
    [selectedAssesmentData?.regional_modifier]
  );

  const totalLicensesForMetrics = useMemo(() => {
    return isEditing
      ? Number(licenses.totalLicenses) || 0
      : Number(selectedAssesmentData?.partner_total_licenses) || 0;
  }, [
    isEditing,
    licenses.totalLicenses,
    selectedAssesmentData?.partner_total_licenses,
  ]);

  const displayExtraPoints = useMemo(
    () => computeExtraPoints(totalLicensesForMetrics),
    [totalLicensesForMetrics, computeExtraPoints]
  );

  const engagementValueSum = useMemo(() => {
    if (!isEditing || !evaluationData?.length) return 0;
    let s = 0;
    evaluationData.forEach((cat: any) => {
      cat.engagements?.forEach((e: any) => {
        s += Number(e.selectedValue ?? 0);
      });
    });
    return s;
  }, [isEditing, evaluationData]);

  const engagementPotentialImprovementSum = useMemo(() => {
    if (!isEditing || !evaluationData?.length) return 0;
    let s = 0;
    evaluationData.forEach((cat: any) => {
      cat.engagements?.forEach((e: any) => {
        s += Number(e.potential_improvement) || 0;
      });
    });
    return s;
  }, [isEditing, evaluationData]);

  const viewPotentialPoints = useMemo(() => {
    if (isEditing) return undefined;
    const v = getPotentialPointsFromRecord(selectedAssesmentData);
    return v === null ? undefined : v;
  }, [isEditing, selectedAssesmentData]);

  const displayTotalMultiplier = useMemo(() => {
    if (isEditing) {
      return engagementValueSum + regionalFixed;
    }
    return Number(selectedAssesmentData?.total_multiplier) || 0;
  }, [
    isEditing,
    engagementValueSum,
    regionalFixed,
    selectedAssesmentData?.total_multiplier,
  ]);

  const acceleratorDisplayMetrics = useMemo((): PointsAcceleratorDisplayMetrics => {
    return {
      extraPointsBasedOnTotalLicenses: displayExtraPoints,
      regionalModifier: regionalFixed,
      totalMultiplier: displayTotalMultiplier,
    };
  }, [displayExtraPoints, regionalFixed, displayTotalMultiplier]);

  useEffect(() => {
    if (!isEditing) return;
    const netNew = Number(licenses.netNewLicenses) || 0;
    const totalMult = displayTotalMultiplier;
    const extra = displayExtraPoints;
    const rm = regionalFixed;
    const totalPoint = (netNew + 10) * totalMult + extra * rm;
    const potentialPoints = engagementPotentialImprovementSum * netNew;
    setLiveTotals({ totalPoints: totalPoint, potentialPoints });
  }, [
    isEditing,
    licenses.netNewLicenses,
    displayTotalMultiplier,
    displayExtraPoints,
    regionalFixed,
    engagementPotentialImprovementSum,
  ]);

  const resetEditStateFromRecord = useCallback(() => {
    const ev = buildEvaluationDataFromRecord(
      partnerEngagementCategoryList,
      selectedAssesmentData
    );
    setEvaluationData(ev);
    const apiTotal = Number(selectedAssesmentData?.partner_total_licenses) || 0;
    const netNew = Number(selectedAssesmentData?.partner_new_licenses) || 0;
    setInitialTotalLicenses(apiTotal);
    setLicenses({
      netNewLicenses: netNew,
      totalLicenses: apiTotal,
    });
    setComment(
      selectedAssesmentData?.comments?.[0]?.comment ?? ""
    );
    setExistingDocuments([...(selectedAssesmentData?.documents ?? [])]);
    setNewFiles([]);
    const fromEngagements = getPotentialPointsFromRecord(
      selectedAssesmentData
    );
    setLiveTotals({
      totalPoints: Number(selectedAssesmentData?.total_partner_points) || 0,
      potentialPoints:
        fromEngagements !== null
          ? fromEngagements
          : Number(selectedAssesmentData?.potential_improvement) || 0,
    });
    const rawDate = selectedAssesmentData?.assessment_date;
    const m = rawDate ? moment(rawDate) : null;
    setAssessmentDateInput(m?.isValid() ? m.format("YYYY-MM-DD") : "");
  }, [partnerEngagementCategoryList, selectedAssesmentData]);

  useEffect(() => {
    if (readOnly) {
      setIsEditing(false);
    }
  }, [readOnly]);

  const handleStartEdit = () => {
    if (readOnly) return;
    resetEditStateFromRecord();
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    dispatch(partnerProgramEventConfigurationSliceAction.setPartnerPointsMutationMessage(""));
  };

  useEffect(() => {
    if (updatePartnerPointsLoading) wasUpdating.current = true;
    if (wasUpdating.current && !updatePartnerPointsLoading) {
      wasUpdating.current = false;
      const raw = partnerPointsMutationMessage || "";
      const msg = raw.toLowerCase();
      if (raw) {
        onMutationToast?.(raw, msg.includes("fail"));
        dispatch(
          partnerProgramEventConfigurationSliceAction.setPartnerPointsMutationMessage(
            ""
          )
        );
      }
      if (msg && !msg.includes("fail")) {
        setIsEditing(false);
        onRefresh?.();
      }
    }
  }, [
    updatePartnerPointsLoading,
    partnerPointsMutationMessage,
    onRefresh,
    onMutationToast,
    dispatch,
  ]);

  useEffect(() => {
    if (deletePartnerPointsLoading) wasDeleting.current = true;
    if (wasDeleting.current && !deletePartnerPointsLoading) {
      wasDeleting.current = false;
      const raw = partnerPointsMutationMessage || "";
      const msg = raw.toLowerCase();
      if (raw) {
        onMutationToast?.(raw, msg.includes("fail"));
        dispatch(
          partnerProgramEventConfigurationSliceAction.setPartnerPointsMutationMessage(
            ""
          )
        );
      }
      if (msg && !msg.includes("fail")) {
        setDeleteModalOpen(false);
        onRefresh?.();
        onBack?.();
      }
    }
  }, [
    deletePartnerPointsLoading,
    partnerPointsMutationMessage,
    onBack,
    onRefresh,
    onMutationToast,
    dispatch,
  ]);

  const setCommentEngagementWise = (commentData: any) => {
    const cData = structuredClone(evaluationData);
    cData[commentMainIndex]["engagements"][commentSubIndex]["comment"] =
      commentData;
    setEvaluationData(cData);
  };

  const onEngagementSelectChange = (
    mainIndex: number,
    subIndex: number,
    value: any,
    accelerator_value: any
  ) => {
    if (!accelerator_value?.length) return;
    const maxValue = Math.max(
      ...accelerator_value.map((o: { option_value: any }) =>
        Number(o.option_value)
      )
    );
    const eData = structuredClone(evaluationData);
    eData[mainIndex]["engagements"][subIndex]["selectedValue"] = value;
    eData[mainIndex]["engagements"][subIndex]["potential_improvement"] = (
      Number(maxValue) - Number(value)
    ).toFixed(2);
    setEvaluationData(eData);
  };

  const buildEngagementPayload = () => {
    const loginUserId =
      loggedUserDetail?.ID ?? userDetail?.ID ?? 0;
    const engagement: any[] = [];
    evaluationData.forEach((data) => {
      data.engagements?.forEach((e_data: any) => {
        engagement.push({
          activity_id: e_data.ID,
          name: e_data.activity_name,
          activity_key: "Yes",
          activity_value: e_data.selectedValue,
          category_id: e_data.category_id,
          potential_improvement: e_data.potential_improvement,
          comment: e_data.comment,
          updated_by: loginUserId,
          action: e_data.action || "Approved",
        });
      });
    });
    return engagement;
  };

  const handleUpdate = () => {
    const id = getPointsRecordId(selectedAssesmentData);
    if (id == null || partnerId == null) return;

    dispatch(
      partnerProgramEventConfigurationSliceAction.setPartnerPointsMutationMessage(
        ""
      )
    );

    const loginUserId =
      loggedUserDetail?.ID ?? userDetail?.ID ?? 0;
    const displayName =
      [loggedUserDetail?.first_name, loggedUserDetail?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      [userDetail?.first_name, userDetail?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      "Admin";

    const payload = {
      partner_id: partnerId,
      tier_id: summaryDat?.tier?.value ?? selectedAssesmentData?.tier_id ?? 0,
      engagements: buildEngagementPayload(),
      total_partner_points: Number(liveTotals.totalPoints),
      documents: [...existingDocuments],
      license_points: Number(
        summaryDat?.next_required_license?.value?.licenses_required ?? 0
      ),
      partner_new_licenses: Number(licenses.netNewLicenses),
      partner_total_licenses: Number(licenses.totalLicenses),
      total_multiplier: displayTotalMultiplier,
      updated_by: loginUserId,
      status: "Active",
      event_type: "submitted",
      action: "submit",
      potential_improvement: liveTotals.potentialPoints,
      comments: [
        {
          role: "Admin",
          commented_by: displayName,
          comment,
        },
      ],
      assessment_date:
        assessmentDateInput.trim() ||
        selectedAssesmentData?.assessment_date,
      next_assessment_date: selectedAssesmentData?.next_assessment_date,
    };

    if (newFiles.length) {
      const fd = new FormData();
      newFiles.forEach((f) => fd.append("documents", f));
      Object.keys(payload).forEach((key) => {
        const val = (payload as any)[key];
        if (val === null || val === undefined) return;
        if (typeof val === "object" || Array.isArray(val)) {
          fd.append(key, JSON.stringify(val));
        } else {
          fd.append(key, String(val));
        }
      });
      dispatch(updatePartnerPoints({ id, partnerId, body: fd }));
    } else {
      dispatch(updatePartnerPoints({ id, partnerId, body: payload as any }));
    }
  };

  const handleConfirmDelete = () => {
    const assessmentId = getPointsRecordId(selectedAssesmentData);
    if (assessmentId == null || partnerId == null) return;
    dispatch(
      partnerProgramEventConfigurationSliceAction.setPartnerPointsMutationMessage(
        ""
      )
    );
    dispatch(deletePartnerPoints({ assessmentId, partnerId }));
  };

  const handleBack = () => {
    if (onBack) onBack();
  };

  const submittedByName =
    selectedAssesmentData?.submitted_by_name ??
    selectedAssesmentData?.submitted_by_user_name ??
    selectedAssesmentData?.created_by_name ??
    resolvedSubmitterName ??
    "—";
  /** Prefer explicit submission timestamps; `updated_at` last so edits do not replace submission date when API sends it. */
  const submittedOnRaw =
    selectedAssesmentData?.submitted_at ??
    selectedAssesmentData?.submitted_on ??
    selectedAssesmentData?.submission_date ??
    selectedAssesmentData?.created_at ??
    selectedAssesmentData?.updated_at;
  const submittedOnDisplay = submittedOnRaw
    ? moment(submittedOnRaw).format("D MMM, YYYY")
    : "—";

  const assessmentDateRaw = selectedAssesmentData?.assessment_date;
  const assessmentDateDisplay =
    assessmentDateRaw && moment(assessmentDateRaw).isValid()
      ? moment(assessmentDateRaw).format("DD MMM YYYY")
      : "";

  const recordId = getPointsRecordId(selectedAssesmentData);

  return (
    <>
    <div className="detail-header">
      <div className="detail-header-action">
        <button className="back-button" type="button" onClick={handleBack}>
          <Iconography icon="arrowLeft" size={IconographySizes.Small} />
          BACK TO OVERVIEW
        </button>
        <div className="detail-header-aside">
            <div className="detail-header-actions detail-header-actions--text">
              {!isEditing && (
                <button
                  type="button"
                  className="detail-action-link"
                  onClick={() => setDeleteModalOpen(true)}
                  disabled={
                    readOnly ||
                    recordId == null ||
                    partnerId == null ||
                    deletePartnerPointsLoading
                  }
                >
                  DELETE
                </button>
              )}
              {isEditing && (
                <Button
                  variant="secondary"
                  label="UPDATE"
                  normalCase
                  onClick={handleUpdate}
                  disabled={
                    readOnly ||
                    updatePartnerPointsLoading ||
                    recordId == null ||
                    partnerId == null
                  }
                />
              )}
              <button
                type="button"
                className="detail-action-link"
                onClick={isEditing ? handleCancelEdit : handleStartEdit}
                disabled={
                  readOnly ||
                  (isEditing
                    ? updatePartnerPointsLoading
                    : recordId == null || partnerId == null)
                }
              >
                {isEditing ? "CANCEL" : "EDIT"}
                {!isEditing && (
                  <Iconography icon="pen" size={IconographySizes.Small} />
                )}
              </button>
            </div>
            
          </div>
          </div>
        <div className="detail-header-main">
          {isEditing ? (
            <div className="detail-header-assessment-row">
              <AssessmentDayPicker
                value={assessmentDateInput}
                onChange={setAssessmentDateInput}
                disabled={readOnly}
              />
            </div>
          ) : (
            <h1>Assessment Day - {assessmentDateDisplay || "—"}</h1>
          )}
          <div className="detail-submitted-meta">
              Submitted by {submittedByName}  <span>•</span>   {submittedOnDisplay}
            </div>
        </div>
      </div>
    <div className="detail-container">
      

      <div className="detail-content">
        <SummarySection
          selectedAssesmentData={selectedAssesmentData}
          summaryDat={summaryDat}
          isEditable={false}
          liveTotals={isEditing ? liveTotals : undefined}
          viewPotentialPoints={viewPotentialPoints}
        />
        <LicensesSection
          selectedAssesmentData={selectedAssesmentData}
          isEditable={isEditing}
          value={
            isEditing
              ? {
                  netNewLicenses: licenses.netNewLicenses,
                  totalLicenses: licenses.totalLicenses,
                }
              : undefined
          }
          onChange={
            isEditing
              ? (next, changedField) => {
                  if (changedField === "netNewLicenses") {
                    setLicenses({
                      netNewLicenses: next.netNewLicenses,
                      totalLicenses:
                        initialTotalLicenses + next.netNewLicenses,
                    });
                  } else {
                    setLicenses(next);
                  }
                }
              : undefined
          }
        />
        <PointsAcceleratorsSection
          displayMetrics={acceleratorDisplayMetrics}
        />
        <div className="detail-section full-width">
          <EngagementActivitiesSection
            selectedAssesmentData={selectedAssesmentData}
            isEditable={isEditing}
            engagementEditData={isEditing ? evaluationData : null}
            onEngagementSelectChange={onEngagementSelectChange}
            onOpenEngagementComment={(
              mainIdx: number,
              subIdx: number,
              item: { comment?: any[]; activity_name?: string }
            ) => {
              setCommentMainIndex(mainIdx);
              setCommentSubIndex(subIdx);
              setSelectedComment(item.comment || []);
              setSelectedActivityName(item.activity_name || "");
              setShowCommentModal(true);
            }}
          />
        </div>
        <div className="detail-section full-width">
          <CommentsFilesSection
            selectedAssesmentData={selectedAssesmentData}
            isEditable={isEditing}
            comment={isEditing ? comment : undefined}
            onCommentChange={isEditing ? setComment : undefined}
            existingDocuments={
              isEditing ? existingDocuments : undefined
            }
            newFiles={isEditing ? newFiles : undefined}
            onAddFiles={
              isEditing
                ? (files) => setNewFiles((p) => [...p, ...files])
                : undefined
            }
            onRemoveNewFile={
              isEditing
                ? (i) =>
                    setNewFiles((p) => p.filter((_, idx) => idx !== i))
                : undefined
            }
            onRemoveExistingDocument={
              isEditing
                ? (i) =>
                    setExistingDocuments((p) =>
                      p.filter((_, idx) => idx !== i)
                    )
                : undefined
            }
          />
        </div>
      </div>

      {deleteModalOpen && (
        <Modal
          width={420}
          onClose={() =>
            !deletePartnerPointsLoading && setDeleteModalOpen(false)
          }
          className="detail-delete-modal"
        >
          <div className="detail-delete-modal-inner">
            <p className="detail-delete-message">
              Delete this evaluation? This cannot be undone.
            </p>
            <div className="detail-delete-actions">
              <Button
                variant="secondary"
                label="Cancel"
                onClick={() => setDeleteModalOpen(false)}
                disabled={deletePartnerPointsLoading}
              />
              <Button
                variant="primary"
                label="Delete"
                onClick={handleConfirmDelete}
                disabled={deletePartnerPointsLoading}
              />
            </div>
          </div>
        </Modal>
      )}

      {showCommentModal && (
        <CommentModal
          open={showCommentModal}
          onClose={() => setShowCommentModal(false)}
          activityName={selectedActivityName}
          comments={selectedComment}
          onAddComment={() => {}}
          onEditComment={() => {}}
          onDeleteComment={() => {}}
          onUpdate={(data: any) => {
            setCommentEngagementWise(data);
          }}
        />
      )}
    </div>
    </>
  );
};

export default Detail;
